<div #toastOutlet></div>

<dialog
  data-testid="userModal"
  class="modal"
  [class.modal-open]="showUserModal()">
  <div class="modal-box w-11/12 max-w-2xl max-h-[90vh] flex flex-col">
    <form [formGroup]="userForm" (submit)="handleSubmit($event)" class="flex flex-col h-full overflow-hidden">
      <!-- Fixed Header -->
      <div class="p-6 pb-0 flex-none">
        <h1 id="user-modal-title" class="font-semibold text-3xl mb-5">{{ getTitle() }}</h1>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto px-6 py-4 shadow-inner rounded-lg">
        <fieldset class="fieldset">
          <!-- First Name / Last Name -->
          <div class="flex sm:flex-row flex-col sm:gap-4">
            <!-- First Name -->
            <div class="flex-col w-full">
              <div class="flex">
                <legend class="fieldset-legend" data-testid="first-name-label">First Name<sup class="text-red-500">*</sup></legend>
              </div>
              <input
                id="first-name-input"
                formControlName="firstName"
                class="w-full input placeholder-gray-300 rounded-lg"
                [class.input-error]="shouldShowError('firstName')"
                [class.input-success]="firstName?.valid && firstName?.touched"
                type="text"
                placeholder="Enter first name"
                (keydown.enter)="$event.preventDefault()"
                autocomplete="given-name"
              />
              <div
                class="text-error text-sm mt-1"
                [class.opacity-100]="shouldShowError('firstName')"
                [class.opacity-0]="!shouldShowError('firstName')">
                {{ getFirstNameError() || 'First name is required' }}
              </div>
            </div>

            <!-- Last Name -->
            <div class="flex-col w-full">
              <div class="flex">
                <legend class="fieldset-legend" data-testid="last-name-label">Last Name<sup class="text-red-500">*</sup></legend>
              </div>
              <input
                id="last-name-input"
                formControlName="lastName"
                class="w-full input placeholder-gray-300 rounded-lg"
                [class.input-error]="shouldShowError('lastName')"
                [class.input-success]="lastName?.valid && lastName?.touched"
                type="text"
                placeholder="Enter last name"
                (keydown.enter)="$event.preventDefault()"
                autocomplete="family-name"
              />
              <div
                class="text-error text-sm mt-1"
                [class.opacity-100]="shouldShowError('lastName')"
                [class.opacity-0]="!shouldShowError('lastName')">
                {{ getLastNameError() || 'Last name is required' }}
              </div>
            </div>
          </div>

          <!-- Email -->
          <div class="flex-col w-full">
            <div class="flex">
              <legend class="fieldset-legend" data-testid="email-label">Email<sup class="text-red-500">*</sup></legend>
            </div>
            <input
              id="email-input"
              formControlName="email"
              class="w-full input placeholder-gray-300 rounded-lg"
              [class.input-error]="shouldShowError('email')"
              [class.input-success]="email?.valid && email?.touched"
              type="email"
              placeholder="mail@myorder.ai"
              (keydown.enter)="$event.preventDefault()"
              autocomplete="email"
            />
            <div
              class="text-error text-sm mt-1"
              [class.opacity-100]="shouldShowError('email')"
              [class.opacity-0]="!shouldShowError('email')">
              {{ getEmailError() || 'Please enter a valid email address' }}
            </div>
          </div>

          <!-- Username / Roles -->
          <div class="flex sm:flex-row flex-col sm:gap-4">
            <!-- Username -->
            <div class="flex-col w-full">
              <div class="flex">
                <legend class="fieldset-legend" data-testid="username-label">Username<sup class="text-red-500">*</sup></legend>
              </div>
              <input
                id="username-input"
                formControlName="username"
                class="w-full input placeholder-gray-300 rounded-lg"
                [class.input-error]="shouldShowError('username')"
                [class.input-success]="username?.valid && username?.touched"
                type="text"
                placeholder="Enter username"
                (keydown.enter)="$event.preventDefault()"
                autocomplete="username"
              />
              <div
                class="text-error text-sm mt-1"
                [class.opacity-100]="shouldShowError('username')"
                [class.opacity-0]="!shouldShowError('username')">
                {{ getUsernameError() || 'Username is required' }}
              </div>
            </div>

            <!-- Roles -->
            <div class="flex-col w-full">
              <div class="flex">
                <legend class="fieldset-legend" data-testid="roles-label">Roles<sup class="text-red-500">*</sup></legend>
              </div>
              <app-select
                [id]="'role-selector'"
                [name]="'role-selector'"
                [items]="selectItems()"
                [(selectedItem)]="selectedRole" />
              <div class="text-error text-sm mt-1 opacity-0">
                Role is required
              </div>
            </div>
          </div>

          <!-- Password / Confirm Password -->
          <div class="flex sm:flex-row flex-col sm:gap-4">
            <!-- Password -->
            <div class="flex-col w-full">
              <div class="flex">
                @if (mode() === 'create') {
                  <legend class="fieldset-legend" data-testid="password-label">Password<sup class="text-red-500">*</sup></legend>
                } @else {
                  <legend class="fieldset-legend" data-testid="new-password-label">New Password</legend>
                }
              </div>
              <input
                id="password-input"
                formControlName="password"
                class="w-full input placeholder-gray-300 rounded-lg"
                [class.input-error]="shouldShowError('password')"
                [class.input-success]="password?.valid && password?.touched && password?.value?.trim()"
                type="password"
                placeholder="Enter password"
                (keydown.enter)="$event.preventDefault()"
                autocomplete="new-password"
              />
              <div
                class="text-error text-sm mt-1"
                [class.opacity-100]="shouldShowError('password')"
                [class.opacity-0]="!shouldShowError('password')">
                {{ getPasswordError() || 'Password is required' }}
              </div>
            </div>

            <!-- Confirm Password -->
            <div class="flex-col w-full">
              <div class="flex">
                @if (mode() === 'create') {
                  <legend class="fieldset-legend" data-testid="confirm-password-label">Confirm Password<sup class="text-red-500">*</sup></legend>
                } @else {
                  <legend class="fieldset-legend" data-testid="new-confirm-password-label">Confirm New Password</legend>
                }
              </div>
              <input
                id="confirm-password-input"
                formControlName="confirmPassword"
                class="w-full input placeholder-gray-300 rounded-lg"
                [class.input-error]="shouldShowError('confirmPassword') || passwordsDoNotMatch() || shouldShowError('password')"
                [class.input-success]="confirmPassword?.valid && confirmPassword?.touched && !passwordsDoNotMatch() && !shouldShowError('password')"
                type="password"
                placeholder="Confirm password"
                (keydown.enter)="$event.preventDefault()"
                autocomplete="new-password"
              />
              <div
                class="text-error text-sm mt-1"
                [class.opacity-100]="shouldShowError('confirmPassword') || passwordsDoNotMatch()"
                [class.opacity-0]="!shouldShowError('confirmPassword') && !passwordsDoNotMatch()">
                {{ getConfirmPasswordError() || 'Please confirm your password' }}
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <!-- Fixed Footer -->
      <div class="p-6 pt-4 flex-none">
        <div class="flex justify-end gap-5">
          <app-button
            [id]="'cancel-button'"
            type="button"
            label="Cancel"
            (clicked)="onCancel()" />
          <app-button
            [id]="'submit-button'"
            color="primary"
            type="submit"
            [label]="getSubmitLabel()"
            [disabled]="!isFormValid() || isSubmitting()"
            [loading]="isSubmitting()" />
        </div>
      </div>
    </form>
  </div>
</dialog>
